import { useState } from "react";
import { P, FF, inputBase } from "../../styles/tokens";
import { Card } from "../ui";
import { useSubscription } from "../../hooks/useSubscription";
import { useLimits } from "../../hooks/useLimits";

/* ── Helpers ── */
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "numeric", month: "long", year: "numeric",
  });
}

function daysUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/** Capitaliza la primera letra de un string */
function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/* ── Features clave por plan (resumen, no la lista completa de landing) ── */
const PLAN_CARDS = {
  basico: {
    label: "Básico",
    price: "$89/mes",
    features: [
      "Presupuestos ilimitados por categoría",
      "Gastos recurrentes automáticos",
      "Metas de ahorro con seguimiento",
      "Historial completo sin límite",
    ],
  },
  pro: {
    label: "Pro",
    price: "$119/mes",
    features: [
      "Todo lo de Básico",
      "Análisis avanzado con gráficas",
      "Duky IA — tu asistente financiero",
      "Reportes exportables",
    ],
  },
};

/* ── Skeleton de carga ── */
function SubscriptionSkeleton() {
  const bar = (w, h = 14, mb = 8) => ({
    width: w, height: h, borderRadius: 6, background: P.border,
    marginBottom: mb, animation: "pulse 1.5s ease-in-out infinite",
  });
  return (
    <Card style={{ padding: "20px 22px" }}>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>
      <div style={bar("60%", 18, 16)} />
      <div style={bar("80%")} />
      <div style={bar("55%")} />
      <div style={{ marginTop: 20, ...bar("40%", 38, 0), borderRadius: 12 }} />
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Modal confirmación genérico (evita window.confirm)
   ══════════════════════════════════════════════════════════════════ */
function ConfirmModal({ title, message, confirmLabel, onClose, onConfirm, loading, danger = true }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px 16px",
      }}
      onClick={(e) => e.target === e.currentTarget && !loading && onClose()}
    >
      <div style={{
        background: P.surface, borderRadius: 20, padding: "28px 24px",
        width: "100%", maxWidth: 400, boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }}>
        <div style={{ fontSize: 17, fontWeight: 900, color: danger ? P.danger.text : P.text, fontFamily: FF, marginBottom: 12 }}>
          {title}
        </div>
        <div style={{ fontSize: 13, color: P.textMd, fontFamily: FF, lineHeight: 1.6, marginBottom: 24 }}>
          {message}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 1, padding: "12px 16px", borderRadius: 12, border: "none",
              background: loading ? P.border : (danger ? P.danger.base : P.success.base),
              color: loading ? P.textSm : "#fff",
              fontFamily: FF, fontSize: 13, fontWeight: 700,
              cursor: loading ? "default" : "pointer",
              transition: "background .15s",
            }}
          >
            {loading ? "Procesando..." : confirmLabel}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: "12px 20px", borderRadius: 12,
              border: `1.5px solid ${P.border}`,
              background: "transparent", color: P.textSm,
              fontFamily: FF, fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Modal eliminación de cuenta — NO SE TOCA
   ══════════════════════════════════════════════════════════════════ */
function DeleteAccountModal({ onClose, onConfirm, loading }) {
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const enabled = confirm === "ELIMINAR";

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px 16px",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: P.surface, borderRadius: 20, padding: "28px 24px",
        width: "100%", maxWidth: 440, boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }}>
        <div style={{ fontSize: 22, marginBottom: 4 }}>⚠️</div>
        <div style={{ fontSize: 17, fontWeight: 900, color: P.danger.text, fontFamily: FF, marginBottom: 12 }}>
          ¿Eliminar tu cuenta?
        </div>
        <div style={{ fontSize: 13, color: P.textMd, fontFamily: FF, lineHeight: 1.6, marginBottom: 20 }}>
          Esto programará la eliminación de tu cuenta. Tienes <strong>30 días para arrepentirte</strong>.
          Después de ese plazo, todos tus datos serán borrados permanentemente.
        </div>

        {/* Motivo opcional */}
        <div style={{ marginBottom: 16 }}>
          <label
            htmlFor="delete-reason"
            style={{ display: "block", fontSize: 11, fontWeight: 700, color: P.textSm, fontFamily: FF, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}
          >
            Motivo (opcional)
          </label>
          <textarea
            id="delete-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="¿Por qué te vas? Tu feedback nos ayuda a mejorar."
            rows={3}
            style={{
              ...inputBase,
              resize: "vertical",
              minHeight: 72,
            }}
          />
        </div>

        {/* Confirmación de texto */}
        <div style={{ marginBottom: 20 }}>
          <label
            htmlFor="delete-confirm"
            style={{ display: "block", fontSize: 11, fontWeight: 700, color: P.danger.text, fontFamily: FF, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}
          >
            Escribe ELIMINAR para confirmar
          </label>
          <input
            id="delete-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="ELIMINAR"
            autoComplete="off"
            style={{
              ...inputBase,
              borderColor: enabled ? P.danger.base : P.border,
              fontWeight: 700,
            }}
          />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => onConfirm(reason || undefined)}
            disabled={!enabled || loading}
            style={{
              flex: 1, padding: "12px 16px", borderRadius: 12, border: "none",
              background: enabled && !loading ? P.danger.base : P.border,
              color: enabled && !loading ? "#fff" : P.textSm,
              fontFamily: FF, fontSize: 13, fontWeight: 700,
              cursor: enabled && !loading ? "pointer" : "default",
              transition: "background .15s",
            }}
          >
            {loading ? "Procesando..." : "Eliminar mi cuenta"}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: "12px 20px", borderRadius: 12,
              border: `1.5px solid ${P.border}`,
              background: "transparent", color: P.textSm,
              fontFamily: FF, fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Barra de progreso para el trial ── */
function TrialProgressBar({ periodStart, periodEnd }) {
  const now = Date.now();
  const start = new Date(periodStart).getTime();
  const end = new Date(periodEnd).getTime();
  const pct = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        height: 8, borderRadius: 99, background: P.border, overflow: "hidden",
      }}>
        <div style={{
          height: "100%",
          width: `${pct}%`,
          borderRadius: 99,
          background: `linear-gradient(90deg, ${P.extras.mid}, ${P.brand.terracota})`,
          transition: "width 0.5s ease",
        }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
        <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>Inicio</span>
        <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>Día 15</span>
      </div>
    </div>
  );
}

/* ── Card de plan (Estado 5 — Free) ── */
function PlanCard({ planKey, trialUsed, onTrial, onCheckout, loading }) {
  const plan = PLAN_CARDS[planKey];

  return (
    <div style={{
      background: P.surface,
      border: `1.5px solid ${P.border}`,
      borderRadius: 16,
      padding: "20px 18px",
      display: "flex",
      flexDirection: "column",
      gap: 0,
      flex: 1,
      minWidth: 0,
    }}>
      {/* Header */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: P.text, fontFamily: FF }}>
          {plan.label}
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, color: P.brand.primary, fontFamily: FF, marginTop: 2 }}>
          {plan.price}
        </div>
      </div>

      {/* Features */}
      <ul style={{ listStyle: "none", padding: 0, margin: "14px 0 20px", display: "flex", flexDirection: "column", gap: 8 }}>
        {plan.features.map((f) => (
          <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: P.textMd, fontFamily: FF, lineHeight: 1.4 }}>
            <span style={{ color: P.success.base, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>✓</span>
            {f}
          </li>
        ))}
      </ul>

      {/* Botones según historial de trial */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: "auto" }}>
        {trialUsed ? (
          <>
            {/* Ya usó trial — solo Pagar */}
            <button
              onClick={() => onCheckout(planKey)}
              disabled={loading}
              style={{
                width: "100%", padding: "12px 16px", borderRadius: 12, border: "none",
                background: loading ? P.border : P.brand.terracota,
                color: loading ? P.textSm : "#fff",
                fontFamily: FF, fontSize: 13, fontWeight: 700,
                cursor: loading ? "default" : "pointer",
                transition: "background .15s",
              }}
            >
              {loading ? "Redirigiendo..." : "Pagar ahora"}
            </button>
            <div style={{ fontSize: 11, color: P.textXs, fontFamily: FF, textAlign: "center", marginTop: 2 }}>
              Ya usaste tu prueba gratuita.
            </div>
          </>
        ) : (
          <>
            {/* Trial disponible */}
            <button
              onClick={() => onTrial(planKey)}
              disabled={loading}
              style={{
                width: "100%", padding: "12px 16px", borderRadius: 12, border: "none",
                background: loading ? P.border : P.success.base,
                color: loading ? P.textSm : "#fff",
                fontFamily: FF, fontSize: 13, fontWeight: 700,
                cursor: loading ? "default" : "pointer",
                transition: "background .15s",
              }}
            >
              {loading ? "Activando..." : "Probar 15 días gratis"}
            </button>
            <div style={{ fontSize: 11, color: P.textXs, fontFamily: FF, textAlign: "center" }}>
              Sin tarjeta de crédito
            </div>
            <button
              onClick={() => onCheckout(planKey)}
              disabled={loading}
              style={{
                width: "100%", padding: "10px 16px", borderRadius: 12,
                border: `1.5px solid ${P.brand.terracota}`,
                background: "transparent",
                color: loading ? P.textSm : P.brand.terracota,
                fontFamily: FF, fontSize: 13, fontWeight: 700,
                cursor: loading ? "default" : "pointer",
                transition: "all .15s",
              }}
            >
              Pagar ahora
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   UsageGrid — muestra uso actual vs límites del plan
   ══════════════════════════════════════════════════════════════════ */
const USAGE_ROWS = [
  { resource: "cuentas",       label: "Cuentas",      limitKey: "max_cuentas" },
  { resource: "tdc",           label: "TDC",          limitKey: "max_tdc" },
  { resource: "metas",         label: "Metas",        limitKey: "max_metas" },
  { resource: "presupuestos",  label: "Presupuestos", limitKey: "max_presupuestos" },
  { resource: "recurrentes",   label: "Recurrentes",  limitKey: "max_recurrentes" },
  { resource: "categorias",    label: "Categorías",   limitKey: "max_categorias" },
  { resource: "msi_activas",   label: "MSI activas",  limitKey: "max_msi_activas" },
  { resource: "duky_messages", label: "Duky (mes)",   limitKey: "duky_messages_month" },
  { resource: "voz_records",   label: "Voz (mes)",    limitKey: "voz_records_month" },
];

function UsageGrid({ limits, usage, isAtLimit, loading }) {
  if (loading) return (
    <div style={{ fontSize: 12, color: P.textXs, fontFamily: FF, padding: "8px 0" }}>
      Cargando uso...
    </div>
  );

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: P.textXs, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: FF, marginBottom: 10 }}>
        Uso actual
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "6px 12px",
      }}>
        {USAGE_ROWS.map(({ resource, label, limitKey }) => {
          const used = usage?.[resource];
          const max  = limits?.[limitKey];
          if (typeof used !== "number" || typeof max !== "number") return null;
          const atLimit = isAtLimit(resource);
          return (
            <div key={resource} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: `1px solid ${P.border}` }}>
              <span style={{ fontSize: 12, color: P.textSm, fontFamily: FF }}>{label}</span>
              <span style={{
                fontSize: 12, fontWeight: 700, fontFamily: FF,
                color: atLimit ? P.danger.text : P.text,
              }}>
                {used}/{max}{atLimit ? " ⚠️" : ""}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   SubscriptionConfig — componente principal
   ══════════════════════════════════════════════════════════════════ */
export default function SubscriptionConfig() {
  const {
    subscription, loading, error, refetch,
    cancel, reactivate, requestDeletion, cancelDeletion,
    startTrial, goToCheckout, goToBillingPortal,
  } = useSubscription();

  const { limits, usage, loading: limitsLoading, isAtLimit } = useLimits();

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError]     = useState("");
  const [actionOk, setActionOk]           = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmModal, setConfirmModal]    = useState(null); // { title, message, confirmLabel, fn }

  /** Wrapper genérico para acciones mutantes */
  async function handleAction(fn, successMsg) {
    setActionError("");
    setActionOk("");
    setActionLoading(true);
    try {
      await fn();
      if (successMsg) {
        setActionOk(successMsg);
        setTimeout(() => setActionOk(""), 3500);
      }
    } catch (e) {
      setActionError(e.message || "Error al procesar la solicitud");
      setTimeout(() => setActionError(""), 4000);
    } finally {
      setActionLoading(false);
    }
  }

  /** Abre el modal de confirmación genérico */
  function askConfirm(title, message, confirmLabel, fn) {
    setConfirmModal({ title, message, confirmLabel, fn });
  }

  async function runConfirm() {
    const fn = confirmModal?.fn;
    setConfirmModal(null);
    if (fn) await fn();
  }

  async function handleDelete(reason) {
    setActionError("");
    setActionLoading(true);
    try {
      await requestDeletion(reason);
      setShowDeleteModal(false);
      setActionOk("Tu cuenta ha sido programada para eliminación. Tienes 30 días para cancelarlo.");
      setTimeout(() => setActionOk(""), 5000);
    } catch (e) {
      setActionError(e.message || "Error al procesar la solicitud");
      setTimeout(() => setActionError(""), 4000);
    } finally {
      setActionLoading(false);
    }
  }

  /* ── Loading ── */
  if (loading) return <SubscriptionSkeleton />;

  /* ── Error crítico ── */
  if (error) return (
    <Card style={{ padding: "20px 22px" }}>
      <div style={{ fontSize: 13, color: P.danger.text, fontFamily: FF, fontWeight: 600 }}>
        No se pudo cargar la información de suscripción: {error}
      </div>
      <button
        onClick={refetch}
        style={{ marginTop: 12, padding: "8px 16px", borderRadius: 10, border: `1.5px solid ${P.border}`, background: P.bg, color: P.textMd, fontFamily: FF, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
      >
        Reintentar
      </button>
    </Card>
  );

  /* ── Derivar campos de la suscripción ── */
  const plan              = subscription?.plan ?? "gratis";
  const status            = subscription?.status ?? "active";
  const periodEnd         = subscription?.current_period_end;
  const periodStart       = subscription?.current_period_start;
  const cancelAtPeriodEnd = subscription?.cancel_at_period_end === true;
  const isTrial           = subscription?.is_trial === true;
  const trialUsedAt       = subscription?.trial_used_at ?? null;
  const stripeSubId       = subscription?.stripe_subscription_id ?? null;
  const deletionAt        = subscription?.deletion_scheduled_for;
  const isPendingDeletion = status === "pending_deletion";
  const isPaidActive      = !isTrial && stripeSubId !== null && status === "active";
  const isPaidCanceled    = !isTrial && cancelAtPeriodEnd === true;
  const isTrialActive     = isTrial === true && status === "active";
  const isFree            = plan === "gratis";

  /* ── Días restantes del trial ── */
  const trialDaysLeft = isTrialActive ? daysUntil(periodEnd) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── Feedback global ── */}
      {actionOk && (
        <div style={{ background: P.success.pastel, border: `1px solid ${P.success.tint}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: P.success.text, fontFamily: FF, fontWeight: 700 }}>
          ✓ {actionOk}
        </div>
      )}
      {actionError && (
        <div style={{ background: P.danger.pastel, border: `1px solid ${P.danger.tint}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: P.danger.text, fontFamily: FF, fontWeight: 700 }}>
          {actionError}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════
          ESTADO 1 — pending_deletion
          ════════════════════════════════════════════════════════ */}
      {isPendingDeletion && (
        <Card style={{ padding: "20px 22px", border: `2px solid ${P.danger.base}`, background: P.danger.pastel }}>
          <div style={{ fontSize: 15, fontWeight: 900, color: P.danger.text, fontFamily: FF, marginBottom: 8 }}>
            ⚠️ Cuenta programada para eliminación
          </div>
          <div style={{ fontSize: 13, color: P.danger.text, fontFamily: FF, lineHeight: 1.6, marginBottom: 16 }}>
            Tu cuenta será eliminada el <strong>{fmtDate(deletionAt)}</strong>
            {daysUntil(deletionAt) !== null && ` (en ${daysUntil(deletionAt)} días)`}.
            Todos tus datos serán borrados permanentemente.
          </div>
          <button
            onClick={() => handleAction(cancelDeletion, "Eliminación cancelada. Tu cuenta está activa.")}
            disabled={actionLoading}
            style={{
              width: "100%", padding: "12px 20px", borderRadius: 12, border: "none",
              background: actionLoading ? P.border : P.success.base,
              color: actionLoading ? P.textSm : "#fff",
              fontFamily: FF, fontSize: 14, fontWeight: 700,
              cursor: actionLoading ? "default" : "pointer",
              transition: "background .15s",
            }}
          >
            {actionLoading ? "Procesando..." : "Cancelar eliminación"}
          </button>
        </Card>
      )}

      {/* ════════════════════════════════════════════════════════
          ESTADO 2 — Plan pago activo (no trial, stripe activo)
          ════════════════════════════════════════════════════════ */}
      {!isPendingDeletion && isPaidActive && !isPaidCanceled && (
        <Card style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: P.textXs, fontFamily: FF, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Tu plan
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: P.text, fontFamily: FF, marginBottom: 4 }}>
            Plan {capitalize(plan)}
          </div>
          {periodEnd && (
            <div style={{ fontSize: 13, color: P.textSm, fontFamily: FF, marginBottom: 4 }}>
              Renueva el <strong style={{ color: P.text }}>{fmtDate(periodEnd)}</strong>
            </div>
          )}
          <UsageGrid limits={limits} usage={usage} isAtLimit={isAtLimit} loading={limitsLoading} />
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Botón primario — Billing Portal */}
            <button
              onClick={() => handleAction(goToBillingPortal)}
              disabled={actionLoading}
              style={{
                width: "100%", padding: "12px 20px", borderRadius: 12, border: "none",
                background: actionLoading ? P.border : P.success.base,
                color: actionLoading ? P.textSm : "#fff",
                fontFamily: FF, fontSize: 14, fontWeight: 700,
                cursor: actionLoading ? "default" : "pointer",
                transition: "background .15s",
              }}
            >
              {actionLoading ? "Abriendo portal..." : "Gestionar suscripción"}
            </button>
            {/* Botón secundario — Cancelar */}
            <button
              onClick={() => askConfirm(
                "¿Cancelar suscripción?",
                `Tu plan ${capitalize(plan)} seguirá activo hasta el ${fmtDate(periodEnd)}. Después pasarás a Free.`,
                "Sí, cancelar",
                () => handleAction(cancel, "Suscripción cancelada. Seguirás con el plan hasta que venza.")
              )}
              disabled={actionLoading}
              style={{
                padding: "10px 20px", borderRadius: 12,
                border: `1.5px solid ${P.danger.tint}`,
                background: P.danger.pastel,
                color: actionLoading ? P.textSm : P.danger.base,
                fontFamily: FF, fontSize: 13, fontWeight: 700,
                cursor: actionLoading ? "default" : "pointer",
                transition: "all .15s",
              }}
            >
              Cancelar suscripción
            </button>
          </div>
        </Card>
      )}

      {/* ════════════════════════════════════════════════════════
          ESTADO 3 — Plan pago cancelado (a fin de ciclo)
          ════════════════════════════════════════════════════════ */}
      {!isPendingDeletion && isPaidCanceled && (
        <Card style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: P.textXs, fontFamily: FF, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Tu plan
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: P.text, fontFamily: FF, marginBottom: 4 }}>
            Plan {capitalize(plan)} — termina el {fmtDate(periodEnd)}
          </div>
          <UsageGrid limits={limits} usage={usage} isAtLimit={isAtLimit} loading={limitsLoading} />
          {/* Warning */}
          <div style={{
            background: P.warn.pastel, border: `1px solid ${P.warn.tint}`,
            borderRadius: 12, padding: "12px 14px", marginBottom: 20,
          }}>
            <div style={{ fontSize: 13, color: P.warn.text, fontFamily: FF, lineHeight: 1.6, fontWeight: 600 }}>
              ⚠️ Después de esa fecha pasarás a Free.
            </div>
          </div>
          <button
            onClick={() => handleAction(reactivate, "¡Suscripción reactivada!")}
            disabled={actionLoading}
            style={{
              width: "100%", padding: "12px 24px", borderRadius: 12, border: "none",
              background: actionLoading ? P.border : P.success.base,
              color: actionLoading ? P.textSm : "#fff",
              fontFamily: FF, fontSize: 13, fontWeight: 700,
              cursor: actionLoading ? "default" : "pointer",
              transition: "background .15s",
            }}
          >
            {actionLoading ? "Procesando..." : "Reactivar suscripción"}
          </button>
        </Card>
      )}

      {/* ════════════════════════════════════════════════════════
          ESTADO 4 — Trial activo
          ════════════════════════════════════════════════════════ */}
      {!isPendingDeletion && isTrialActive && (
        <Card style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: P.textXs, fontFamily: FF, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Período de prueba
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: P.text, fontFamily: FF, marginBottom: 4 }}>
            Estás en trial {capitalize(plan)}
          </div>
          <div style={{ fontSize: 14, color: trialDaysLeft <= 3 ? P.danger.text : P.textSm, fontWeight: trialDaysLeft <= 3 ? 700 : 400, fontFamily: FF, marginBottom: 8 }}>
            {trialDaysLeft !== null
              ? trialDaysLeft > 0
                ? `${trialDaysLeft} días restantes (termina el ${fmtDate(periodEnd)})`
                : "Tu trial vence hoy"
              : `Termina el ${fmtDate(periodEnd)}`
            }
          </div>

          <UsageGrid limits={limits} usage={usage} isAtLimit={isAtLimit} loading={limitsLoading} />

          {/* Barra de progreso */}
          {periodStart && periodEnd && (
            <TrialProgressBar periodStart={periodStart} periodEnd={periodEnd} />
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Botón primario terracota — Pagar */}
            <button
              onClick={() => handleAction(() => goToCheckout(plan))}
              disabled={actionLoading}
              style={{
                width: "100%", padding: "13px 20px", borderRadius: 12, border: "none",
                background: actionLoading ? P.border : P.brand.terracota,
                color: actionLoading ? P.textSm : "#fff",
                fontFamily: FF, fontSize: 14, fontWeight: 700,
                cursor: actionLoading ? "default" : "pointer",
                transition: "background .15s",
              }}
            >
              {actionLoading ? "Redirigiendo..." : "Pagar para continuar"}
            </button>

            {/* Botón link-style — Cancelar trial */}
            <button
              onClick={() => askConfirm(
                "¿Cancelar trial?",
                `Seguirás usando ${capitalize(plan)} hasta el ${fmtDate(periodEnd)}. Después pasarás a Free automáticamente.`,
                "Sí, cancelar trial",
                () => handleAction(cancel, "Trial cancelado. Seguirás en el plan hasta que venza.")
              )}
              disabled={actionLoading}
              style={{
                background: "none", border: "none",
                color: actionLoading ? P.textXs : P.danger.base,
                fontFamily: FF, fontSize: 13, fontWeight: 600,
                cursor: actionLoading ? "default" : "pointer",
                textDecoration: "underline",
                padding: "4px 0",
                alignSelf: "center",
              }}
            >
              Cancelar trial
            </button>
          </div>
        </Card>
      )}

      {/* ════════════════════════════════════════════════════════
          ESTADO 5 — Free
          ════════════════════════════════════════════════════════ */}
      {!isPendingDeletion && isFree && !isTrialActive && (
        <Card style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: P.textXs, fontFamily: FF, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Tu plan
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: P.text, fontFamily: FF, marginBottom: 4 }}>
            Plan Gratis
          </div>
          <div style={{ fontSize: 13, color: P.textSm, fontFamily: FF, marginBottom: 8, lineHeight: 1.5 }}>
            Mejora para desbloquear análisis avanzado, Duky IA y mucho más.
          </div>
          <UsageGrid limits={limits} usage={usage} isAtLimit={isAtLimit} loading={limitsLoading} />

          {/* Grid de planes — columna en mobile, lado a lado en desktop */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}>
            <style>{`
              @media (min-width: 640px) {
                .plan-grid { flex-direction: row !important; }
              }
            `}</style>
            <div className="plan-grid" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {["basico", "pro"].map((planKey) => (
                <PlanCard
                  key={planKey}
                  planKey={planKey}
                  trialUsed={trialUsedAt !== null}
                  loading={actionLoading}
                  onTrial={(p) => handleAction(() => startTrial(p), `¡Trial de ${capitalize(p)} activado! Disfruta 15 días gratis.`)}
                  onCheckout={(p) => handleAction(() => goToCheckout(p))}
                />
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* ════ Sección eliminación de cuenta (siempre visible) ════ */}
      <Card style={{ padding: "18px 20px", border: `1.5px solid ${P.danger.tint}` }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: P.danger.text, fontFamily: FF, marginBottom: 6 }}>
          🗑️ Eliminar cuenta
        </div>
        <div style={{ fontSize: 12, color: P.textSm, fontFamily: FF, lineHeight: 1.6, marginBottom: 14 }}>
          Esto programará la eliminación permanente de tu cuenta y todos tus datos. Tendrás 30 días para cancelarlo.
        </div>
        <button
          onClick={() => setShowDeleteModal(true)}
          style={{
            padding: "10px 20px", borderRadius: 12,
            border: `1.5px solid ${P.danger.tint}`,
            background: P.danger.pastel, color: P.danger.base,
            fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}
        >
          Eliminar mi cuenta
        </button>
      </Card>

      {/* Modal de confirmación genérico */}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          loading={actionLoading}
          onClose={() => !actionLoading && setConfirmModal(null)}
          onConfirm={runConfirm}
          danger={true}
        />
      )}

      {/* Modal eliminación de cuenta */}
      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => !actionLoading && setShowDeleteModal(false)}
          onConfirm={handleDelete}
          loading={actionLoading}
        />
      )}
    </div>
  );
}
